import React, { createRef, useEffect } from 'react'
import { EmbedCode } from './EmbedCode'
import Logo from '../images/rula_logo_white.svg'
import parse from 'html-react-parser'
import * as E from '../elementor'
import AniLink from 'gatsby-plugin-transition-link/AniLink'
import SmoothScrollLink from './transition/smooth-scroll-link'
import { v4 as uuidv4 } from 'uuid'
import { useFlags } from 'gatsby-plugin-launchdarkly'
import { getReportingAdapter } from '../reporting/get-reporting-adapter'
import { EventPrefix, Trigger } from '@pathccm/common-analytics/events'

interface HalfAndHalfProps {
  section: E.Section
  theme?: string
  imageClass?: string
  template?: string
}

interface Freshpaint {
  track: Function
}

declare const freshpaint: Freshpaint

const HalfAndHalf: React.FC<HalfAndHalfProps> = ({ section, theme = 'sky', imageClass = '', template }) => {
  const { heading, textEditor, image, button, html = [], testimonial } = E.collect(section)

  const formPresent = html?.length > 0 && html[0].html !== null
  const componentIdAttr = formPresent ? 'section-half-and-half' : 'section-half-and-half-without-form'

  const backgroundImage = testimonial.length > 0
    ? 'linear-gradient(to bottom, transparent 50%, black 100%), url(' + (image[0]?.image?.fileUrl ?? '') + ')'
    : 'url(' + (image[0]?.image?.fileUrl ?? '') + ')'

  const uuid: string = uuidv4()
  const formRef = createRef<HTMLDivElement>()
  const insuranceExclusionList = ['I Don’t Plan to Use Insurance', "I Don't See Mine", 'Blue Cross Blue Shield', 'Empire Blue Cross Blue Shield']

  const { marketingSiteSegmentTransition } = useFlags()
  const reporting = getReportingAdapter(marketingSiteSegmentTransition)

  const getElementValue = (id: string): string => {
    const element = document.getElementById(id) as HTMLInputElement | HTMLSelectElement
    return element?.value ?? null
  }

  const checkIsMinor = (): boolean => {
    const birthdate = getElementValue('Contact.Birthdate')
    const date = new Date(birthdate)
    const diff = Date.now() - date.getTime()
    const diffDate = new Date(diff)
    const year = diffDate.getUTCFullYear()
    const age = Math.abs(year - 1970)
    const isMinor = Boolean(age < 18)

    return isMinor
  }

  const submitAnalyticsToFreshpaint = (): void => {
    const minor = checkIsMinor()

    const selectedLocation = getElementValue('Contact.Patient_Location__c')

    const selectedInsurance = getElementValue('Contact.Insurance_Carrier__c')

    const therapyInput = document?.querySelector('[data-ff-ext-radio-checked = "true"]') as HTMLSelectElement
    const selectedTherapyType = therapyInput?.getAttribute('data-value')

    const eventData = {
      event: `upsert_${uuid}`,
      minor,
      insurance: selectedInsurance,
      location: selectedLocation,
      therapyType: selectedTherapyType
    }

    if (!insuranceExclusionList.includes(selectedInsurance)) {
      freshpaint.track('patient_insurance_conversion', eventData)
    }
  }

  // check the form wrapper elements, set a click event listener on the 'Next' button to update values
  const checkElements = (mutationsList: MutationRecord[]): void => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const addedNodes = mutation.addedNodes
        for (const node of addedNodes) {
          if (node instanceof Element && node.id === 'btnnext' && marketingSiteSegmentTransition !== undefined) {
            const submitButton = document.getElementById('btnsubmit') as HTMLButtonElement
            const continueButton = document.getElementById('btnnext') as HTMLButtonElement
            submitButton?.addEventListener('click', submitAnalyticsToFreshpaint)
            submitButton?.addEventListener('click', () => {
              reporting.track({
                eventName: {
                  prefix: EventPrefix.MARKETING_SITE,
                  trigger: Trigger.FORM_SUBMITTED
                },
                properties: {}
              })
                .then(() => { return null })
                .catch((err: Error) => { console.error('HalfAndHalf Template Reporting Error: ', err) })
            })

            continueButton?.addEventListener('click', () => {
              reporting.track({
                eventName: {
                  prefix: EventPrefix.MARKETING_SITE,
                  trigger: Trigger.FORM_INTERACTION
                },
                properties: {
                  form_name: 'formstack',
                  interaction_type: 'nextBtnClick'
                }
              })
                .then(() => { return null })
                .catch((err: Error) => { console.error('HalfAndHalf Template Reporting Error: ', err) })
            })
          }
        }
      }
    }
  }

  useEffect(() => {
    const observer = new MutationObserver(checkElements)
    const config = { childList: true, subtree: true }
    const formWrapper = formRef.current;

    (formWrapper != null) && observer.observe(formWrapper, config)

    return () => {
      observer.disconnect()
    }
  }, [marketingSiteSegmentTransition])

  interface TextBlockProps {
    heading: E.HeadingWidget[]
    textEditor: E.TextEditorWidget[]
  }

  const renderTextBlocks: React.FC<TextBlockProps> = ({ heading, textEditor }) => {
    const reactElement: JSX.Element[] = []
    for (let i = 0; i < heading.length; i++) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (!heading[i + 1] && !textEditor[i]) {
        continue
      }

      if (template !== null && template === 'therapy-type') {
        reactElement.push(
          <div className='text-block'>
            <p className='paragraph-bold'>{parse(heading[i + 1]?.title ?? '')}</p>
            <p className='paragraph'>{textEditor[i]?.text()}</p>
          </div>
        )
      } else {
        reactElement.push(
          <div className='text-block'>
            <h3>{parse(heading[i + 1]?.title ?? '')}</h3>
            <p>{textEditor[i]?.parse()}</p>
          </div>
        )
      }
    }
    return reactElement
  }

  return (
    <div className={`half-and-half-wrapper ${imageClass}`} id={componentIdAttr} data-theme={theme}>
      <div className={`half-and-half-content ${html.length > 0 ? 'form' : ''}`}>
        {formPresent
          ? (
            <div className='form-block' ref={formRef}>
              <h2>{parse(heading[0]?.title ?? '')}</h2>
              <p className='paragraph'>{textEditor[0]?.parse()}</p>
              <div className='form-container'>
                <EmbedCode fragment={html[0]?.html ?? ''} />
              </div>
            </div>
            )
          : (
            <>
              <div className='text-blocks-wrapper'>
                <h2>{parse(heading[0].title ?? '')}</h2>
                {renderTextBlocks({ heading, textEditor })}
                {button[0] !== undefined
                  ? (
                    <>
                      {button[0]?.link?.includes('#') ?? false
                        ? (
                          <SmoothScrollLink
                            id={button[0]?.link !== undefined ? button[0].link : '#section-half-and-half'}
                            link_id={button[0]?.button_css_id}
                            offset={0}
                            offsetMobile={0}
                          >
                            <button className='rula-button'>{button[0] !== undefined ? button[0].text : 'Get Started'}</button>
                          </SmoothScrollLink>
                          )
                        : (
                          <AniLink href={button[0]?.link}>
                            <button className='rula-button'>{button[0] !== undefined ? button[0].text : 'Get Started'}</button>
                          </AniLink>
                          )}
                    </>
                    )
                  : null}
              </div>
            </>
            )}
      </div>
      <div className={`half-and-half-image ${imageClass}`} style={{ backgroundImage, backgroundSize: 'cover' }}>
        <img className='large-image' src={image[0]?.image?.fileUrl} alt={image[0]?.image?.altText} />
        <img className='logo' src={Logo} alt='Rula Logo' />
        <div className='testimonial'>
          <p className='content'>{parse(testimonial[0]?.testimonials[0]?.content ?? '')}</p>
          <p className='author'>{parse(testimonial[0]?.testimonials[0]?.name ?? '')}</p>
          <p className='title'>{parse(testimonial[0]?.testimonials[0]?.title ?? '')}</p>
        </div>
      </div>
    </div>
  )
}

export default HalfAndHalf
